import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../state/store'
import { fmt } from '../engine/decimal'
import { tapValue } from '../engine/formulas'
import { LETTERS_BY_ID } from '../config/letters'
import { GLOBAL } from '../config/global'

interface FloatingInk {
  id: number
  x: number
  y: number
  value: string
}

interface Splat {
  id: number
  x: number
  y: number
}

let nextId = 0

export function TapPad() {
  const tap = useGame((s) => s.tap)
  const letters = useGame((s) => s.letters)
  const taps = useGame((s) => s.totalTaps)
  const heat = useGame((s) => s.heat)
  const weight = useGame((s) => s.weight)
  const perTap = tapValue(letters)
  const owned = letters.filter((l) => l.level > 0)

  const [floaters, setFloaters] = useState<FloatingInk[]>([])
  const [splats, setSplats] = useState<Splat[]>([])
  const padRef = useRef<HTMLDivElement>(null)

  let glyph = 'E'
  if (owned.length > 0) {
    const best = [...owned].sort((a, b) => b.level - a.level)[0]
    glyph = LETTERS_BY_ID[best.id]?.letter ?? 'E'
  }

  const ownedCount = owned.length
  const phase = ownedCount === 0 ? 'blank' : 'type'

  const heatPct = Math.max(0, Math.min(100, ((heat - GLOBAL.heatMin) / (GLOBAL.heatMax - GLOBAL.heatMin)) * 100))
  const weightPct = Math.max(0, Math.min(100, ((weight - GLOBAL.weightMin) / (GLOBAL.weightMax - GLOBAL.weightMin)) * 100))

  const handleTap = useCallback((e: React.MouseEvent) => {
    tap()

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left + (Math.random() - 0.5) * 40
    const y = e.clientY - rect.top - 10
    const id = nextId++
    setFloaters((prev) => [...prev, { id, x, y, value: `+${fmt(perTap)}` }])
    setTimeout(() => setFloaters((prev) => prev.filter((f) => f.id !== id)), 800)

    const splatCount = 3 + Math.floor(Math.random() * 3)
    const newSplats: Splat[] = []
    for (let i = 0; i < splatCount; i++) {
      newSplats.push({
        id: nextId++,
        x: x + (Math.random() - 0.5) * 30,
        y: y + Math.random() * 20,
      })
    }
    setSplats((prev) => [...prev, ...newSplats])
    setTimeout(() => {
      setSplats((prev) => prev.filter((s) => !newSplats.find((ns) => ns.id === s.id)))
    }, 600)
  }, [tap, perTap])

  return (
    <div ref={padRef} className="relative rounded-lg border border-ink-border bg-ink-panel p-4 text-center">
      {/* Heat gauge (top) */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between text-[10px]">
          <span className="text-teal-400">Heat</span>
          <span className="tabular-nums text-ink-muted">{Math.round(heat)}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-ink-border">
          <div
            className="h-full rounded-full bg-teal-400 transition-all duration-300"
            style={{ width: `${heatPct}%` }}
          />
        </div>
      </div>

      {/* Tap button */}
      <motion.button
        whileTap={{ scale: 0.92, y: 2 }}
        onClick={handleTap}
        className="chip mx-auto h-36 w-36 select-none border-ink-tealdim bg-ink-bg text-6xl text-ink-teal shadow-glow active:shadow-none"
      >
        [{glyph}]
      </motion.button>

      {/* Floating +Ink numbers */}
      <AnimatePresence>
        {floaters.map((f) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 1, y: f.y, x: f.x }}
            animate={{ opacity: 0, y: f.y - 50 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="pointer-events-none absolute text-sm font-bold text-ink-teal"
            style={{ left: 0, top: 0 }}
          >
            {f.value}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Ink splat particles */}
      <AnimatePresence>
        {splats.map((s) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0.8, scale: 1 }}
            animate={{ opacity: 0, scale: 0.3 }}
            transition={{ duration: 0.6 }}
            className="pointer-events-none absolute h-1.5 w-1.5 rounded-full bg-ink-teal"
            style={{ left: s.x, top: s.y }}
          />
        ))}
      </AnimatePresence>

      {/* Weight gauge (bottom) */}
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[10px]">
          <span className="text-amber-400">Press</span>
          <span className="tabular-nums text-ink-muted">{Math.round(weight)}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-ink-border">
          <div
            className="h-full rounded-full bg-amber-400 transition-all duration-300"
            style={{ width: `${weightPct}%` }}
          />
        </div>
      </div>

      <div className="mt-3 text-xs text-ink-muted">
        {phase === 'blank' ? 'Tap the quill' : `+${fmt(perTap)} Ink`} · {taps} taps
      </div>
    </div>
  )
}
